import * as React from 'react';

import { DetailsTableCell } from '@amzn/imdb-shared-meridian-components/components/DetailsTable/DetailsTableCell';
import { CustomSingleCellDetailsTableRow } from '@amzn/imdb-shared-meridian-components/components/DetailsTable/DetailsTableRow';
import { NewWindowLink } from '@amzn/imdb-shared-meridian-components/components/link';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Table from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { ListDetails, ListFlag } from '../../../../../listoramaAdmin-api/generated-src/api';
import { getListWebsiteUrl } from '../../../../../utils/url';

type ListMainDetailsCellId =
    | 'Name'
    | 'Description'
    | 'State'
    | 'Visibility'
    | 'URL'
    | 'Flags'
    | 'Created'
    | 'Last Modified';

export interface ListMainDetailsTableProps {
    list: ListDetails;
}

export const ListMainDetailsTable: React.FC<ListMainDetailsTableProps> = (props: ListMainDetailsTableProps) => {
    const { list } = props;
    const listMainDetailsTableRows = createTableRows(list);

    return (
        <Table
            data-test-id='listMainDetailsTable'
            showDividers={true}
            spacing='small'
            rowComponents={[CustomSingleCellDetailsTableRow]}
        >
            {listMainDetailsTableRows.map((row) => (
                <CustomSingleCellDetailsTableRow
                    key={row.name}
                    header={row.name}
                    data={row.data}
                    getTableCell={getListMainDetailsTableCell}
                />
            ))}
        </Table>
    );
};

interface CustomTableRowProps<T extends string> {
    name: T;
    data: string;
}

// Serialized into the Flags row's string data (see createFlagsCellData).
interface FlagCellEntry {
    flag: string;
    source?: string;
    isOverridden?: boolean;
    modified?: string;
}

const getListMainDetailsTableCell = (cellId: ListMainDetailsCellId, data: string) => {
    switch (cellId) {
        case 'Flags': {
            const flags = JSON.parse(data) as FlagCellEntry[];
            if (flags.length === 0) {
                return <DetailsTableCell data='No flags' />;
            }
            return (
                <DetailsTableCell
                    CustomDataElement={
                        <Column spacing='200'>
                            {flags.map((entry) => (
                                <span key={entry.flag} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                    {entry.flag}
                                    {entry.source && (
                                        <Text type='b200' color='secondary'>
                                            {`by ${entry.source}${entry.modified ? ` on ${entry.modified}` : ''}`}
                                        </Text>
                                    )}
                                    {entry.isOverridden && (
                                        <Tooltip
                                            position='end'
                                            title={
                                                'A human decision that automation respects: automated ' +
                                                're-classification will not silently undo this flag change.'
                                            }
                                        >
                                            <Box type='outline' backgroundColor='SteelBlue'>
                                                OVERRIDE
                                            </Box>
                                        </Tooltip>
                                    )}
                                </span>
                            ))}
                        </Column>
                    }
                />
            );
        }
        case 'URL': {
            return (
                <DetailsTableCell
                    data-test-id='listLink'
                    CustomDataElement={<NewWindowLink url={data} text={data} />}
                />
            );
        }
        case 'Visibility': {
            const [visibility, suppressed] = data.split('|');
            return (
                <DetailsTableCell
                    CustomDataElement={
                        <span style={{ display: 'flex', gap: '6px' }}>
                            {visibility}
                            {suppressed === 'true' && (
                                <Tooltip
                                    position='end'
                                    title={
                                        'Lists marked with a spam flag are treated as "private-by-default", ' +
                                        'and are not displayed to users other than the owner.'
                                    }
                                >
                                    <Box type='outline' backgroundColor='Maroon'>
                                        SUPPRESSED
                                    </Box>
                                </Tooltip>
                            )}
                        </span>
                    }
                />
            );
        }
        default:
            return <DetailsTableCell data={data} />;
    }
};

/**
 * Pairs each current flag with its moderation attributes (who set it, whether a human overrode
 * automation, and when), so CS reads the answer next to the flag itself. Attributes are absent for
 * flags written before attribution rolled out (DISCO-21397).
 */
const createFlagsCellData = (list: ListDetails): FlagCellEntry[] =>
    list.flags.map((flag) => ({
        flag,
        source: list.flagAttributes?.[flag]?.source,
        isOverridden: list.flagAttributes?.[flag]?.isOverridden,
        modified: list.flagAttributes?.[flag]?.modified
    }));

const createTableRows = (list: ListDetails): CustomTableRowProps<ListMainDetailsCellId>[] => [
    {
        name: 'Name',
        data: list.name
    },
    {
        name: 'Description',
        data: list.originalDescription ? list.originalDescription : 'No description'
    },
    {
        name: 'State',
        data: list.state
    },
    {
        name: 'Visibility',
        data: `${list.publik ? 'Public' : 'Private'}|${list.flags.includes(ListFlag.Spam)}`
    },
    {
        name: 'URL',
        data: getListWebsiteUrl(list.listId)
    },
    {
        name: 'Flags',
        data: JSON.stringify(createFlagsCellData(list))
    },
    {
        name: 'Created',
        data: list.created
    },
    {
        name: 'Last Modified',
        data: list.modified
    }
];
